import { ProcessGeneral } from 'api/endpoints/processes';
import { TmsConnectionDetails } from 'api/endpoints/tmsConnections';
import BaseRepo from 'container/BaseRepo';
import {
  buildBadRequestApiError,
  buildNotFoundApiError,
} from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';
import { SearchRequest, SearchRequestTyped } from 'container/search';
import {
  FinishTaskValues,
  OrderEntryScenario,
  OrderEntryTask,
  OrderEntryTaskBase,
  OrderEntryTasksToRetry,
  OrderEntryTaskToRetry,
  SearchOrderEntryTasksRequest,
  TruckMateClientModel,
  TruckMateCommoditiesModel,
} from 'features/orderEntry/types';

import {
  CreateExtractionPlanRequest,
  ExtractionPlan,
  SearchExtractionPlanRequest,
  TmsDataModel,
  UpdateExtractionPlanType,
} from '../../shared/licensedModules/types';
import {
  CreateOrderEntryScenarioRequest,
  OrderEntrySearchResult,
  SearchOrderEntryScenariosRequest,
  UpdateScenarioRequestType,
} from '../types';
import { IOrderEntryRepo } from './IOrderEntryRepo';

export class OrderEntryRepo extends BaseRepo implements IOrderEntryRepo {
  searchTruckmateCommodities = (
    scenario: OrderEntryScenario,
    searchRequest: SearchRequestTyped<TruckMateCommoditiesModel>
  ): ApiResponseAsync<TruckMateCommoditiesModel[]> => {
    const link = scenario._links
      ? scenario._links['getTruckmateCommodities']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get truckmate commodities" is not available')
        )
      );

    return this.helpers.executeSearch<TruckMateCommoditiesModel[]>(
      link,
      searchRequest
    );
  };
  getTaskByIdExtended = (
    task: OrderEntryTaskBase | OrderEntryTaskToRetry
  ): ApiResponseAsync<OrderEntryTask> => {
    const link = task._links ? task._links['self'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "self" is not available'))
      );

    return this.helpers.handleGet<OrderEntryTask>(link.href, {
      responseStopTransformPaths: ['data.field_values', 'data.classify_result'],
    });
  };
  searchTruckmateClients = (
    scenario: OrderEntryScenario,
    searchRequest: SearchRequestTyped<TruckMateClientModel>
  ): ApiResponseAsync<TruckMateClientModel[]> => {
    const link = scenario._links
      ? scenario._links['getTruckmateClients']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get truckmate clients" is not available')
        )
      );

    return this.helpers.executeSearch<TruckMateClientModel[]>(
      link,
      searchRequest
    );
  };

  moveTaskToReviewReady = (
    task: OrderEntryTaskToRetry
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['sendTaskForReview'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "move to review ready" is not available')
        )
      );
    }

    return this.helpers.handlePostAction(link.href, task);
  };

  lockTask = (
    task: OrderEntryTaskBase
  ): ApiResponseAsync<OrderEntryTaskBase | undefined> => {
    const link = task._links ? task._links['lock'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "lock" is not available'))
      );
    }

    return this.helpers.handlePost(link.href, task);
  };
  skipTask = (
    task: OrderEntryTaskBase
  ): ApiResponseAsync<OrderEntryTaskBase | undefined> => {
    const link = task._links ? task._links['skip'] : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "skip" is not available'))
      );
    }

    return this.helpers.handlePost(link.href, task);
  };

  searchScenarios = async (
    draft: ProcessGeneral,
    request: SearchOrderEntryScenariosRequest
  ): ApiResponseAsync<OrderEntryScenario[]> => {
    const link = draft._links
      ? draft._links['searchOrderEntryScenarios']
      : undefined;

    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "search order entry scenarios" is not available')
        )
      );
    }

    return this.helpers
      .executeSearch<OrderEntrySearchResult>(link, request)
      .then((searchResult) => {
        if (isApiError(searchResult)) {
          return searchResult;
        }
        if (!searchResult) searchResult = [];
        return searchResult;
      });
  };

  lockNextDocument = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<OrderEntryTaskBase | undefined> => {
    const link = draft._links
      ? draft._links['lockNextOrderEntryDocument']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "lock next order entry document" is not available')
        )
      );

    return this.helpers.handlePost<undefined, OrderEntryTaskBase | undefined>(
      link.href,
      undefined
    );
  };

  getTaskById = async (
    draft: ProcessGeneral,
    taskId: string
  ): ApiResponseAsync<OrderEntryTaskBase> => {
    const link = draft._links
      ? draft._links['searchOrderEntryTasks']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search order entry tasks by id" is not available')
      );

    const request: SearchRequest = {
      top: 1,
      filter: {
        operator: 'AND',
        items: [
          {
            field: 'id',
            operation: 'equal',
            value: taskId,
            not: false,
          },
        ],
      },
    };

    const result = await this.helpers.executeSearch<OrderEntryTaskBase[]>(
      link,
      request
    );

    if (isApiError(result)) return result;

    if (!result || result.length === 0)
      return buildNotFoundApiError(
        new Error('no order entry task by id was found')
      );

    return result[0];
  };

  searchOrderEntryExtractionPlans = async (
    draft: ProcessGeneral,
    request: SearchExtractionPlanRequest
  ): ApiResponseAsync<ExtractionPlan[]> => {
    const link = draft._links
      ? draft._links['searchOrderEntryExtractionPlans']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search order entry extraction forms" is not available')
      );

    const result = await this.helpers.executeSearch<ExtractionPlan[]>(
      link,
      request,
      {
        convertRequestToSnakeCase: true,
      }
    );

    if (isApiError(result)) return result;

    if (!result || result.length === 0) return [];

    return result;
  };

  createScenario = async (
    draft: ProcessGeneral,
    request: CreateOrderEntryScenarioRequest
  ): ApiResponseAsync<OrderEntryScenario> => {
    const link = draft._links
      ? draft._links['createOrderEntryScenario']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "create order entry scenario request" is not available')
      );

    return this.helpers.handlePost<
      CreateOrderEntryScenarioRequest,
      OrderEntryScenario
    >(link.href, request);
  };

  updateScenario = async (
    scenario: OrderEntryScenario,
    request: UpdateScenarioRequestType
  ): ApiResponseAsync<OrderEntryScenario> => {
    const link = scenario._links ? scenario._links['update'] : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "update" is not available')
      );

    return this.helpers.executePatch(link, request);
  };

  setActivateScenario = async (
    scenario: OrderEntryScenario,
    activate: boolean
  ): ApiResponseAsync<OrderEntryScenario> => {
    let link = undefined;
    if (!scenario.isActive && activate && scenario._links?.['activate']) {
      link = scenario._links['activate'];
    } else if (
      scenario.isActive &&
      !activate &&
      scenario._links?.['deactivate']
    ) {
      link = scenario._links['deactivate'];
    }

    if (!link)
      return buildBadRequestApiError(
        new Error(
          `link "${activate ? 'activate' : 'deactivate'}" is not available`
        )
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  deleteScenario = async (
    scenario: OrderEntryScenario
  ): ApiResponseAsync<void> => {
    const link = scenario._links ? scenario._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "delete scenario" is not available')
        )
      );

    return this.helpers.handleDelete(link.href);
  };

  createExtractionPlan = async (
    draft: ProcessGeneral,
    request: CreateExtractionPlanRequest
  ): ApiResponseAsync<ExtractionPlan> => {
    const link = draft._links
      ? draft._links['createOrderEntryExtractionPlan']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "create order entry extraction plan" is not available'
          )
        )
      );

    return this.helpers.handlePost<CreateExtractionPlanRequest, ExtractionPlan>(
      link.href,
      request
    );
  };

  deleteExtractionPlan = async (
    extractionPlan: ExtractionPlan
  ): ApiResponseAsync<void> => {
    const link = extractionPlan._links
      ? extractionPlan._links['delete']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };

  updateExtractionPlan = async (
    extractionPlan: ExtractionPlan,
    request: UpdateExtractionPlanType
  ): ApiResponseAsync<ExtractionPlan> => {
    const link = extractionPlan._links
      ? extractionPlan._links['update']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "update" is not available'))
      );

    return this.helpers.executePatch(link, request);
  };

  searchOrderEntryTasks = async (
    draft: ProcessGeneral,
    request: SearchOrderEntryTasksRequest
  ): ApiResponseAsync<OrderEntryTaskBase[]> => {
    const link = draft._links
      ? draft._links['searchOrderEntryTasks']
      : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "search order entry tasks" is not available')
      );

    const result = await this.helpers.executeSearch<OrderEntryTaskBase[]>(
      link,
      request
    );

    if (isApiError(result)) return result;

    if (!result || result.length === 0) return [];

    return result;
  };

  retryOrderEntryTask = async (
    task: OrderEntryTaskToRetry
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['executeTmsCreateOrder'] : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "executeTmsCreateOrder" is not available')
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  deleteOrderEntryTask = async (
    task: OrderEntryTaskBase | OrderEntryTaskToRetry
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };

  unlockTask = async (task: OrderEntryTaskBase): ApiResponseAsync<void> => {
    const link = task._links ? task._links['cancel'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "cancel" is not available'))
      );

    return this.helpers.handlePostAction(link.href, task);
  };

  finishTask = async (
    values: FinishTaskValues,
    task: OrderEntryTaskBase
  ): ApiResponseAsync<void> => {
    const link = task._links ? task._links['finish'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "finish" is not available'))
      );
    return this.helpers.handlePostAction(link.href, values, {
      requestStopTransformPaths: ['data'],
    });
  };

  getOrderEntryModel = async (
    tms: TmsConnectionDetails
  ): ApiResponseAsync<TmsDataModel> => {
    const link = tms._links ? tms._links['getOrderEntryModel'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get order entry model" is not available')
        )
      );

    const url = new URL(link.href);
    url.searchParams.append('tms_version', '1');
    return this.helpers.handleGet(url.toString());
  };

  getOrderEntryTasksToRetry = (
    draft: ProcessGeneral
  ): ApiResponseAsync<OrderEntryTasksToRetry[]> => {
    const link = draft._links
      ? draft._links['getOrderEntryTasksToRetry']
      : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get order entry tasks to retry" is not available')
        )
      );

    return this.helpers.handleGet<OrderEntryTasksToRetry[]>(link.href);
  };
}
